import type { StoryNode, StoryProject } from './types'

export type SearchOptions = {
  caseSensitive?: boolean
  wholeWord?: boolean
  includePlanning?: boolean
  nodeId?: string
}
export type SearchField =
  | 'title'
  | 'chapter'
  | 'synopsis'
  | 'notes'
  | 'text'
  | 'speaker'
  | 'direction'
  | 'choice'
export type StoryMatch = {
  id: string
  nodeId: string
  nodeTitle: string
  field: SearchField
  itemId?: string
  label: string
  value: string
  start: number
  end: number
}

function searchPattern(query: string, options: SearchOptions) {
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(
    options.wholeWord
      ? `(?<![\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])`
      : escaped,
    options.caseSensitive ? 'gu' : 'giu',
  )
}

function fields(node: StoryNode, planning: boolean) {
  const values: Array<{
    field: SearchField
    itemId?: string
    label: string
    value: string
  }> = [{ field: 'title', label: 'Scene title', value: node.title }]
  for (const block of node.blocks) {
    values.push({
      field: 'text',
      itemId: block.id,
      label: block.kind === 'dialogue' ? 'Dialogue' : 'Action',
      value: block.text,
    })
    if (block.kind === 'dialogue')
      values.push(
        {
          field: 'speaker',
          itemId: block.id,
          label: 'Character name',
          value: block.speaker ?? '',
        },
        {
          field: 'direction',
          itemId: block.id,
          label: 'Direction',
          value: block.direction ?? '',
        },
      )
  }
  for (const choice of node.choices)
    values.push({
      field: 'choice',
      itemId: choice.id,
      label: 'Player choice',
      value: choice.label,
    })
  if (planning)
    values.push(
      { field: 'chapter', label: 'Chapter', value: node.chapter ?? '' },
      {
        field: 'synopsis',
        label: 'Scene intention',
        value: node.synopsis ?? '',
      },
      { field: 'notes', label: 'Writer’s notes', value: node.notes ?? '' },
    )
  return values
}

export function findStoryMatches(
  project: StoryProject,
  query: string,
  options: SearchOptions = {},
): StoryMatch[] {
  if (!query) return []
  const pattern = searchPattern(query, options)
  const matches: StoryMatch[] = []
  for (const node of project.nodes) {
    if (options.nodeId && node.id !== options.nodeId) continue
    for (const field of fields(node, Boolean(options.includePlanning))) {
      for (const match of field.value.matchAll(pattern)) {
        matches.push({
          ...field,
          nodeId: node.id,
          nodeTitle: node.title,
          start: match.index,
          end: match.index + match[0].length,
          id: [node.id, field.field, field.itemId ?? '', match.index].join(':'),
        })
      }
    }
  }
  return matches
}

function replaceField(
  node: StoryNode,
  match: StoryMatch,
  value: string,
): StoryNode {
  if (match.field === 'choice')
    return {
      ...node,
      choices: node.choices.map((choice) =>
        choice.id === match.itemId ? { ...choice, label: value } : choice,
      ),
    }
  if (['text', 'speaker', 'direction'].includes(match.field))
    return {
      ...node,
      blocks: node.blocks.map((block) =>
        block.id === match.itemId ? { ...block, [match.field]: value } : block,
      ),
    }
  return { ...node, [match.field]: value }
}

export function replaceStoryMatches(
  project: StoryProject,
  matches: StoryMatch[],
  replacement: string,
): StoryProject {
  if (!matches.length) return project
  const byNode = new Map<string, Map<string, StoryMatch[]>>()
  for (const match of matches) {
    const groups = byNode.get(match.nodeId) ?? new Map<string, StoryMatch[]>()
    const key = [match.field, match.itemId ?? ''].join(':')
    const group = groups.get(key) ?? []
    group.push(match)
    groups.set(key, group)
    byNode.set(match.nodeId, groups)
  }
  let changed = false
  const nodes = project.nodes.map((original) => {
    let node = original
    for (const group of byNode.get(original.id)?.values() ?? []) {
      const first = group[0]
      if (first.nodeId !== node.id) continue
      const current = fields(node, true).find(
        (field) => field.field === first.field && field.itemId === first.itemId,
      )
      // A stale preview must never replace different text that arrived after it was shown.
      if (!current || group.some((match) => match.value !== current.value))
        continue
      const parts: string[] = []
      let offset = 0
      for (const match of [...group].sort((a, b) => a.start - b.start)) {
        if (
          match.start < offset ||
          match.end > current.value.length ||
          match.start >= match.end
        )
          continue
        parts.push(current.value.slice(offset, match.start), replacement)
        offset = match.end
      }
      parts.push(current.value.slice(offset))
      const value = parts.join('')
      if (value !== current.value) {
        changed = true
        node = replaceField(node, first, value)
      }
    }
    return node
  })
  return changed ? { ...project, nodes } : project
}

export function matchFocusId(match: StoryMatch) {
  if (match.field === 'choice') return 'choice-' + match.itemId
  if (['text', 'speaker', 'direction'].includes(match.field))
    return match.field + '-' + match.itemId
  if (match.field === 'title') return 'node-title-' + match.nodeId
  return 'scene-' + match.field
}
